import React, { useState } from 'react';
import { useWallet } from '../components/WalletContext';
import { addSubmission, isUsingFirebase } from '../services/firebase';
import {
  FaUpload,
  FaImage,
  FaCheckCircle,
  FaTimes,
  FaWallet,
  FaFileAlt,
  FaLink,
  FaTwitter,
  FaTelegram,
  FaInfoCircle,
  FaCopy,
  FaTrash,
  FaPlus,
  FaStar,
  FaBullhorn,
  FaShieldAlt,
  FaRocket,
  FaCalendarAlt,
  FaCoins,
  FaGlobe
} from 'react-icons/fa';
import '../styles/SubmitMaterialsPage.css';

// Service types that require material submission
const SERVICES_REQUIRING_MATERIALS = {
  launch_boost: {
    name: 'Launch Boost',
    icon: FaBullhorn,
    requirements: [
      { id: 'banner', label: 'Homepage Banner', specs: '1400x400px, PNG/JPG, max 500KB', type: 'image' },
      { id: 'logo', label: 'Project Logo', specs: '400x400px, PNG (transparent bg preferred)', type: 'image' },
      { id: 'description', label: 'Short Description', specs: 'Max 280 characters', type: 'text' },
      { id: 'twitter', label: 'Twitter Link', specs: 'Full URL to your Twitter', type: 'url' },
      { id: 'telegram', label: 'Telegram Link', specs: 'Full URL to your Telegram', type: 'url' }
    ]
  },
  verified_creator: {
    name: 'Verified Creator',
    icon: FaShieldAlt,
    requirements: [
      { id: 'collection', label: 'Collection Asset ID', specs: 'Your main collection (e.g., MYCOL-1A2B)', type: 'text' },
      { id: 'twitter', label: 'Twitter Profile', specs: 'Full URL to your Twitter profile', type: 'url' },
      { id: 'telegram', label: 'Telegram Contact', specs: 'Your Telegram username or group link', type: 'url' },
      { id: 'proof', label: 'Proof of Ownership', specs: 'Tweet or post proving you own this wallet', type: 'url' }
    ]
  },
  drops_listing: {
    name: 'Drops Page Listing',
    icon: FaRocket,
    requirements: [
      { id: 'banner', label: 'Project Banner', specs: '1920x600px, PNG/JPG, max 1MB', type: 'image' },
      { id: 'logo', label: 'Project Logo', specs: '400x400px, PNG (transparent bg preferred)', type: 'image' },
      { id: 'collection', label: 'Collection Asset ID', specs: 'Your collection (e.g., MYCOL-1A2B)', type: 'text' },
      { id: 'description', label: 'Full Project Description', specs: 'Story, utility, roadmap - max 2000 chars', type: 'textarea' },
      { id: 'dropDate', label: 'Drop Date & Time (UTC)', specs: 'e.g., 2024-03-15 18:00 UTC', type: 'text' },
      { id: 'mintPrice', label: 'Mint Price', specs: 'e.g., 500 KLV or Free', type: 'text' },
      { id: 'supply', label: 'Total Supply', specs: 'e.g., 10,000 NFTs', type: 'text' },
      { id: 'twitter', label: 'Twitter Link', specs: 'Full URL to your project Twitter', type: 'url' },
      { id: 'telegram', label: 'Telegram Link', specs: 'Full URL to your Telegram group', type: 'url' },
      { id: 'website', label: 'Website URL (Optional)', specs: 'Your project website', type: 'url', optional: true }
    ]
  }
};

const SubmitMaterialsPage = () => {
  const { isConnected, walletAddress, connectWallet } = useWallet();

  // Form state
  const [selectedService, setSelectedService] = useState('');
  const [txHash, setTxHash] = useState('');
  const [collectionId, setCollectionId] = useState('');
  const [materials, setMaterials] = useState({});
  const [uploadedImages, setUploadedImages] = useState({});
  const [submitState, setSubmitState] = useState(''); // '', 'submitting', 'success', 'error'
  const [submitMessage, setSubmitMessage] = useState('');

  // Handle image upload (convert to base64 for localStorage storage)
  const handleImageUpload = (requirementId, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (max 500KB)
    if (file.size > 500 * 1024) {
      alert('File too large! Maximum size is 500KB. Please compress your image.');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (PNG, JPG, etc.)');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setUploadedImages(prev => ({
        ...prev,
        [requirementId]: {
          name: file.name,
          size: file.size,
          type: file.type,
          data: reader.result // base64 encoded
        }
      }));
    };
    reader.readAsDataURL(file);
  };

  // Remove uploaded image
  const handleRemoveImage = (requirementId) => {
    setUploadedImages(prev => {
      const updated = { ...prev };
      delete updated[requirementId];
      return updated;
    });
  };

  // Handle text/url input
  const handleMaterialChange = (requirementId, value) => {
    setMaterials(prev => ({
      ...prev,
      [requirementId]: value
    }));
  };

  // Validate form
  const validateForm = () => {
    if (!selectedService) return 'Please select a service';
    if (!txHash) return 'Please enter your transaction hash';
    if (!collectionId && selectedService === 'launch_boost') return 'Please enter your collection ID';

    const service = SERVICES_REQUIRING_MATERIALS[selectedService];
    for (const req of service.requirements) {
      // Skip optional fields
      if (req.optional) continue;

      if (req.type === 'image' && !uploadedImages[req.id]) {
        return `Please upload: ${req.label}`;
      }
      if ((req.type === 'text' || req.type === 'url' || req.type === 'textarea') && !materials[req.id]) {
        return `Please fill in: ${req.label}`;
      }
    }

    return null;
  };

  // Submit materials
  const handleSubmit = async () => {
    const error = validateForm();
    if (error) {
      setSubmitState('error');
      setSubmitMessage(error);
      setTimeout(() => { setSubmitState(''); setSubmitMessage(''); }, 3000);
      return;
    }

    setSubmitState('submitting');
    setSubmitMessage('Saving submission...');

    try {
      // Build submission object
      const submission = {
        id: `sub_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        service: selectedService,
        serviceName: SERVICES_REQUIRING_MATERIALS[selectedService].name,
        wallet: walletAddress,
        txHash: txHash,
        collectionId: collectionId,
        materials: materials,
        images: uploadedImages,
        submittedAt: Date.now(),
        status: 'pending' // pending, approved, rejected
      };

      // Save to Firebase (falls back to localStorage if not configured)
      await addSubmission(submission);

      setSubmitState('success');
      const storageNote = isUsingFirebase() ? '' : ' (Note: Using local storage - configure Firebase for production)';
      setSubmitMessage(`Materials submitted successfully! Our team will review within 24-48 hours.${storageNote}`);

      // Reset form after delay
      setTimeout(() => {
        setSelectedService('');
        setTxHash('');
        setCollectionId('');
        setMaterials({});
        setUploadedImages({});
        setSubmitState('');
        setSubmitMessage('');
      }, 5000);

    } catch (error) {
      console.error('Submission error:', error);
      setSubmitState('error');
      setSubmitMessage('Failed to submit. Please try again.');
    }
  };

  // Copy wallet address
  const copyWallet = () => {
    navigator.clipboard.writeText(walletAddress);
  };

  if (!isConnected) {
    return (
      <div className="submit-materials-page">
        <div className="submit-connect-prompt">
          <FaUpload className="connect-icon" />
          <h2>Submit Materials</h2>
          <p>Connect your wallet to submit materials for your purchased service.</p>
          <button className="connect-btn" onClick={connectWallet}>
            <FaWallet /> Connect Wallet
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="submit-materials-page">
      <div className="submit-container">
        {/* Header */}
        <div className="submit-header">
          <h1><FaUpload /> Submit Materials</h1>
          <p>Upload your project materials after purchasing a service</p>
        </div>

        {/* Instructions */}
        <div className="submit-instructions">
          <FaInfoCircle />
          <div>
            <strong>How it works:</strong>
            <ol>
              <li>Purchase a service from the <a href="/creator">Creator Page</a></li>
              <li>Copy your transaction hash from the success message</li>
              <li>Fill in this form with your materials</li>
              <li>Our team reviews and activates your service within 24-48 hours</li>
            </ol>
          </div>
        </div>

        {/* Wallet Info */}
        <div className="wallet-info">
          <span className="wallet-label">Your Wallet:</span>
          <span className="wallet-address">{walletAddress}</span>
          <button className="copy-btn" onClick={copyWallet}><FaCopy /></button>
        </div>

        {/* Service Selection */}
        <div className="form-section">
          <label className="form-label">Select Service *</label>
          <div className="service-options">
            {Object.entries(SERVICES_REQUIRING_MATERIALS).map(([key, service]) => {
              const Icon = service.icon;
              return (
                <button
                  key={key}
                  className={`service-option ${selectedService === key ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedService(key);
                    setMaterials({});
                    setUploadedImages({});
                  }}
                >
                  <Icon />
                  <span>{service.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Transaction Hash */}
        <div className="form-section">
          <label className="form-label">Transaction Hash *</label>
          <input
            type="text"
            className="form-input"
            placeholder="Enter the transaction hash from your service purchase"
            value={txHash}
            onChange={(e) => setTxHash(e.target.value)}
          />
          <span className="form-hint">Find this in your purchase confirmation or on Kleverscan</span>
        </div>

        {/* Collection ID (for Launch Boost) */}
        {selectedService === 'launch_boost' && (
          <div className="form-section">
            <label className="form-label">Collection Asset ID *</label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g., MYCOLLECTION-1A2B"
              value={collectionId}
              onChange={(e) => setCollectionId(e.target.value)}
            />
          </div>
        )}

        {/* Dynamic Requirements */}
        {selectedService && (
          <div className="requirements-section">
            <h3>Required Materials</h3>

            {SERVICES_REQUIRING_MATERIALS[selectedService].requirements.map((req) => (
              <div key={req.id} className="requirement-item">
                <label className="form-label">
                  {req.label} {req.optional ? '' : '*'}
                  <span className="specs">({req.specs})</span>
                </label>

                {req.type === 'image' ? (
                  <div className="image-upload-area">
                    {uploadedImages[req.id] ? (
                      <div className={`uploaded-image ${req.id === 'banner' ? 'banner-preview' : ''}`}>
                        <img src={uploadedImages[req.id].data} alt={req.label} />
                        <div className="uploaded-image-footer">
                          <div className="image-info">
                            <span className="image-name">{uploadedImages[req.id].name}</span>
                            <span className="image-size">
                              {(uploadedImages[req.id].size / 1024).toFixed(1)} KB
                            </span>
                          </div>
                          <button
                            className="remove-image-btn"
                            onClick={() => handleRemoveImage(req.id)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <label className="upload-label">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(req.id, e)}
                          hidden
                        />
                        <FaImage />
                        <span>Click to upload {req.label}</span>
                        <span className="upload-specs">{req.specs}</span>
                      </label>
                    )}
                  </div>
                ) : req.type === 'url' ? (
                  <div className="url-input-wrapper">
                    <FaLink className="input-icon" />
                    <input
                      type="url"
                      className="form-input with-icon"
                      placeholder={req.optional ? 'https://... (optional)' : 'https://...'}
                      value={materials[req.id] || ''}
                      onChange={(e) => handleMaterialChange(req.id, e.target.value)}
                    />
                  </div>
                ) : req.type === 'textarea' ? (
                  <textarea
                    className="form-textarea large"
                    placeholder={`Enter ${req.label.toLowerCase()}...`}
                    value={materials[req.id] || ''}
                    onChange={(e) => handleMaterialChange(req.id, e.target.value)}
                    maxLength={req.specs.includes('2000') ? 2000 : 1000}
                    rows={6}
                  />
                ) : (
                  <input
                    type="text"
                    className="form-input"
                    placeholder={`Enter ${req.label.toLowerCase()}...`}
                    value={materials[req.id] || ''}
                    onChange={(e) => handleMaterialChange(req.id, e.target.value)}
                    maxLength={req.specs.includes('280') ? 280 : 500}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Submit Status */}
        {submitState && (
          <div className={`submit-status ${submitState}`}>
            {submitState === 'submitting' && <div className="spinner"></div>}
            {submitState === 'success' && <FaCheckCircle />}
            {submitState === 'error' && <FaTimes />}
            <span>{submitMessage}</span>
          </div>
        )}

        {/* Submit Button */}
        <button
          className="submit-btn"
          onClick={handleSubmit}
          disabled={!selectedService || submitState === 'submitting'}
        >
          {submitState === 'submitting' ? (
            <>Submitting...</>
          ) : (
            <><FaCheckCircle /> Submit Materials</>
          )}
        </button>

        {/* Contact Info */}
        <div className="contact-section">
          <p>Questions? Contact us:</p>
          <div className="contact-links">
            <a href="https://t.me/RareCanvasSupport" target="_blank" rel="noopener noreferrer">
              <FaTelegram /> @RareCanvasSupport
            </a>
            <a href="https://twitter.com/RareCanvasNFT" target="_blank" rel="noopener noreferrer">
              <FaTwitter /> @RareCanvasNFT
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmitMaterialsPage;
